import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle, Play } from 'lucide-react';
import { crmFeatures } from '../data/mockData';
import {
  FeatureIcon1,
  FeatureIcon2,
  FeatureIcon3,
  FeatureIcon4,
  FeatureIcon5,
  FeatureIcon6,
  FeatureIcon7,
  FeatureIcon8,
  FeatureIcon9
} from '../components/Icons/Icons'

const ProductDetailPage = () => {
  const { featureId } = useParams();
  // Old product names that now point to renamed pages
  const PRODUCT_ALIASES = {
    'Seamless Collaboration & Integration': 'Intelligent Marketing & Asset Management',
    'AI-Powered Insights & Predictions': 'ML-Powered Insights & Predictions',
  };
  const feature = crmFeatures.find(f => f.title === featureId);
  if (!feature && PRODUCT_ALIASES[featureId]) {
    return <Navigate to={`/products/${PRODUCT_ALIASES[featureId]}`} replace />;
  }

  if (!feature) {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="text-center">
            <h1 className="display-md text-white mb-6">Feature Not Found</h1>
            <Link to="/products">
              <Button className="btn-primary">Back to Products</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const getIcon = (iconName) => {
    const icons = {
      FeatureIcon1,
      FeatureIcon2,
      FeatureIcon3,
      FeatureIcon4,
      FeatureIcon5,
      FeatureIcon6,
      FeatureIcon7,
      FeatureIcon8,
      FeatureIcon9
    };
    return icons[iconName] || FeatureIcon1;
  };

  const IconComponent = getIcon(feature.icon);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container">
          <div className="mb-8">
            <Link to="/products" className="inline-flex items-center text-green-400 hover:text-green-300 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Products
            </Link>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-green-400/10 text-green-400 mb-4">Premium Feature</Badge>
              <h1 className="display-lg text-white mb-6">{feature.title}</h1>
              <p className="body-lg mb-8">{feature.description}</p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/demo">
                  <Button className="btn-primary glow-effect">
                    See Demo
                    <Play className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-secondary">
                    Talk to Expert
                  </Button>
                </Link>
              </div>

              <div className="space-y-4">
                <h3 className="h3 text-white">Key Benefits:</h3>
                {feature.detailsBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                    <span className="body-md text-slate-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-green-400/20 to-transparent rounded-2xl p-8">
                <div className="h-full bg-slate-800 rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <IconComponent className="h-32 w-32 text-green-400 mx-auto mb-6" />
                    <h3 className="h2 text-white mb-2">{feature.title}</h3>
                    {/* <p className="body-md text-slate-400">Powered by Advanced AI</p> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">Real-World Use Cases</h2>
            <p className="body-lg max-w-2xl mx-auto">
              {feature.useCaseDescription}
            </p>
          </div>

          <div className="card-grid-3">
            {feature.useCase.map((useCase, index) => (
              <Card key={index} className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">{useCase.title}</CardTitle>
                  <CardDescription className="body-md">
                    {useCase.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-green-400/10 rounded-lg p-4">
                    <p className="body-md font-semibold text-green-400">{useCase.result}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">How It Works</h2>
            <p className="body-lg max-w-2xl mx-auto">
              {feature.howItWorksIntro || 'Four steps from setup to everyday use.'}
            </p>
          </div>

          <div className={`grid md:grid-cols-${feature.howItworks.length} gap-8`}>
            {feature.howItworks.map((how, index) => (
              <div className="text-center" key={index}>
                <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-400">{how.id}</span>
                </div>
                <h3 className="h3 text-white mb-2">{how.title}</h3>
                <p className="body-sm text-slate-400">
                  {how.description}
                </p>
              </div>))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {/* <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <div className="w-20 h-20 rounded-full bg-green-400/10 flex items-center justify-center mx-auto mb-6">
                <IconComponent className="h-10 w-10 text-green-400" />
              </div>
              <blockquote className="text-2xl font-medium text-white mb-6 italic">
                "{currentTestimonial.quote}"
              </blockquote>
              <div>
                <p className="h3 text-white">{currentTestimonial.author}</p>
                <p className="body-md text-slate-400">{currentTestimonial.title}</p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">
              {feature.readyToTitle}
            </h2>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              {feature.readyToDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Schedule Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/products">
                <Button className="btn-secondary">
                  Explore All Features
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default ProductDetailPage;